import React, { useCallback, useEffect, useState } from "react";
import { Box, Breadcrumbs, Container, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import ButtonBlue from "../../../shared/general/ButtonBlue";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DatePickerCustom from "../../../shared/date/datePicker";
import CardStyle from "../../../shared/general/Card";
import { Link } from "react-router-dom";
import { getSummaryOvertimeShift } from "../../../../../actions/report";
import TableAll from "../../../admin/report/summary-ot-shift/tableAll";
import TableIndividual from "../../../admin/report/summary-ot-shift/tableIndividual";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import CompanyService from "../../../../../services/company.service";

const StyledRoot = styled(Box)({
  backgroundColor: "#FFFFFF !important",
  "& .search-date-container": {
    marginTop: "24px",
    display: "flex",
    flexWrap: "nowrap",
    gap: "16px",
    "& .search-date-box": {
      maxWidth: "350px",
      width: "100%",
    },
    "& .search-button": {
      marginTop: "29px",
      display: "flex",
      alignItems: "center",
    },
  },
  ["@media only screen and (max-width: 600px)"]: {
    "& .search-date-container": {
      flexDirection: "column",
      "& .search-date-box": {
        maxWidth: "100%",
      },
      "& .search-button": {
        marginTop: "0",
        width: "100%",
      },
    },
  },
});

const StyledWrapDataGrid = styled(Box)({
  '& .column-info[role="columnheader"]': {
    backgroundColor: "#283593",
    color: "#FFFFFF",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
  '& .column-ot[role="columnheader"]': {
    backgroundColor: "#7c4dff",
    color: "#FFFFFF",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
  '& .column-shift[role="columnheader"]': {
    backgroundColor: "#2196f3",
    color: "#FFFFFF",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
  '& .column-total-addition[role="columnheader"]': {
    backgroundColor: "#009688",
    color: "#FFFFFF",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
  '& .column-deduction[role="columnheader"]': {
    backgroundColor: "#e53935",
    color: "#FFFFFF",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
  '& .column-net[role="columnheader"]': {
    backgroundColor: "#ffd600",
    color: "#313f4c",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#313f4c",
    },
  },
  '& .column-working-hours[role="columnheader"]': {
    backgroundColor: "#FFFF00",
    color: "#313f4c",
    fontWeight: "500",
    "& .dx-header-filter": {
      color: "#FFFFFF",
    },
  },
});

const ManagerSummaryOTShift = () => {
  const { t } = useTranslation();
  const { result: userProfile } = useSelector((state) => state.userProfile);

  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [isFetchingIndividual, setIsFetchingIndividual] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [idVendor, setIdVendor] = useState(null);
  const [reportAllList, setReportAllList] = useState(null);
  const [reportIndividualList, setReportIndividualList] = useState(null);
  const [searchedFilter, setSearchedFilter] = useState(null);
  const [individualSearchedFilter, setIndividualSearchedFilter] = useState(null);

  const [filter, setFilter] = useState({
    start: dayjs().set("date", 1).toDate(),
    end: dayjs().set("date", dayjs().daysInMonth()).toDate(),
    mode: "all",
  });

  useEffect(() => {
    const fetchVendorContract = async () => {
      try {
        const response = await CompanyService.getVendorContract();
        if (response && response.data && response.data.length > 0) {
          setIdVendor(response.data[0].idVendor);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchVendorContract();
  }, []);

  const fetchAllData = useCallback(async (params) => {
    try {
      setIsFetchingAll(true);
      const response = await getSummaryOvertimeShift(params);
      if (response && response.data) {
        setReportAllList(response.data);
      } else {
        setReportAllList(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetchingAll(false);
    }
  }, []);

  const fetchIndividualData = useCallback(async (params) => {
    try {
      setIsFetchingIndividual(true);
      const response = await getSummaryOvertimeShift(params);
      if (response && response.data) {
        setReportIndividualList(response.data);
      } else {
        setReportIndividualList(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetchingIndividual(false);
    }
  }, []);

  const handleClickSearch = async () => {
    if (!idVendor || !userProfile) return;
    setIsSearched(true);
    setIsSearching(true);
    try {
      if (filter.mode === "all") {
        setSearchedFilter({
          start: filter.start,
          end: filter.end,
        });
        await fetchAllData({
          idCompany: userProfile.idCompany,
          idVendor: idVendor,
          start: dayjs(filter.start).format("YYYY-MM-DD"),
          end: dayjs(filter.end).format("YYYY-MM-DD"),
        });
      } else {
        setIndividualSearchedFilter((prev) => ({
          ...prev,
          start: filter.start,
          end: filter.end,
        }));
        await fetchIndividualData({
          idCompany: userProfile.idCompany,
          idVendor: idVendor,
          idEmp: filter.idEmp,
          start: dayjs(filter.start).format("YYYY-MM-DD"),
          end: dayjs(filter.end).format("YYYY-MM-DD"),
        });
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleClickIndividual = useCallback(
    async (selected) => {
      if (!idVendor || !userProfile) return;
      const idEmp =
        selected && selected.row && selected.row.data
          ? selected.row.data.idEmp
          : undefined;
      setFilter((prev) => ({
        ...prev,
        mode: "individual",
        idEmp,
      }));
      setIndividualSearchedFilter({
        start: searchedFilter && searchedFilter.start,
        end: searchedFilter && searchedFilter.end,
        employee: selected.row.data,
      });
      await fetchIndividualData({
        idCompany: userProfile.idCompany,
        idVendor: idVendor,
        idEmp,
        start: searchedFilter && dayjs(searchedFilter.start).format("YYYY-MM-DD"),
        end: searchedFilter && dayjs(searchedFilter.end).format("YYYY-MM-DD"),
      });
    },
    [idVendor, userProfile, searchedFilter, fetchIndividualData]
  );

  return (
    <StyledRoot className="page">
      <Container maxWidth="lg" style={{ paddingTop: "24px", paddingBottom: "24px" }}>
        <Box marginBottom="24px">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link style={{ textDecoration: "none", color: "inherit" }} to={"/manager/report"}>
              {`${t("AllReports")}`}
            </Link>
            <Typography color="text.primary">{`${t("SummaryOTandShift")}`}</Typography>
          </Breadcrumbs>
        </Box>

        <Typography variant="h4">{t("SummaryOTandShift")}</Typography>

        <Box className="search-date-container">
          <Box className="search-date-box">
            <Typography fontWeight="600" fontSize="14px" paddingBottom="8px" color="text.third">
              {t("StartDate")}
            </Typography>
            <DatePickerCustom
              inputFormat="D MMM YYYY"
              disableMaskedInput
              value={filter.start}
              views={["year", "month", "day"]}
              onChange={(newValue) => {
                if (dayjs(filter.end).diff(dayjs(newValue), "day") > 62) {
                  setFilter((prev) => ({
                    ...prev,
                    start: newValue,
                    end: dayjs(newValue).add(60, "day"),
                  }));
                } else {
                  setFilter((prev) => ({
                    ...prev,
                    start: newValue,
                  }));
                }
              }}
              inputProps={{ readOnly: true }}
              disabled={isSearching || isFetchingAll || isFetchingIndividual}
            />
          </Box>
          <Box className="search-date-box">
            <Typography fontWeight="600" fontSize="14px" paddingBottom="8px" color="text.third">
              {t("EndDate")}
            </Typography>
            <DatePickerCustom
              minDate={filter.start}
              maxDate={dayjs(filter.start).add(62, "day")}
              inputFormat="D MMM YYYY"
              disableMaskedInput
              value={filter.end}
              views={["year", "month", "day"]}
              onChange={(newValue) => {
                setFilter((prev) => ({
                  ...prev,
                  end: newValue,
                }));
              }}
              inputProps={{ readOnly: true }}
              disabled={isSearching || isFetchingAll || isFetchingIndividual}
            />
          </Box>
          <Box className="search-button">
            <ButtonBlue
              startIcon={<SearchRoundedIcon />}
              variant="contained"
              fullWidth
              onClick={handleClickSearch}
              disabled={isSearching || isFetchingAll || isFetchingIndividual || !idVendor}
            >
              {t("Search")}
            </ButtonBlue>
          </Box>
        </Box>

        {filter.mode === "individual" && (
          <Box marginTop="24px">
            <ButtonBlue
              variant="text"
              startIcon={<ArrowBackIosNewOutlinedIcon />}
              onClick={() =>
                setFilter((prev) => ({
                  ...prev,
                  mode: "all",
                }))
              }
            >
              {t("Back")}
            </ButtonBlue>
          </Box>
        )}

        <Box marginTop="24px">
          <CardStyle>
            <Box padding="24px">
              <StyledWrapDataGrid>
                {!isSearched ? (
                  <Typography fontSize="20px" textAlign="center" fontStyle="oblique" color="text.secondary">
                    {t("NoDataPleaseSelectDatesAndSearch")}
                  </Typography>
                ) : (
                  <>
                    {filter.mode === "all" && (
                      <TableAll
                        handleClickIndividual={handleClickIndividual}
                        ReportSummaryOvertimeShiftList={reportAllList}
                        isFetching={isFetchingAll}
                        searchedFilter={searchedFilter}
                      />
                    )}
                    {filter.mode === "individual" && (
                      <TableIndividual
                        ReportSummaryOvertimeShiftIndividual={reportIndividualList}
                        start={individualSearchedFilter && individualSearchedFilter.start}
                        end={individualSearchedFilter && individualSearchedFilter.end}
                        isFetching={isFetchingIndividual}
                        searchedFilter={individualSearchedFilter}
                      />
                    )}
                  </>
                )}
              </StyledWrapDataGrid>
            </Box>
          </CardStyle>
        </Box>
      </Container>
    </StyledRoot>
  );
};

export default ManagerSummaryOTShift;
